import { createPortal } from "react-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Button from "./common/Button";
import { formatReviewHtml } from "../utilities/formatReviewHtml";

interface EnhanceCompareModalProps {
  isOpen: boolean;
  onClose: () => void;
  originalContent: string;
  enhancedContent: string;
  onAccept: () => void;
  onDiscard: () => void;
}

const EnhanceCompareModal = ({
  isOpen,
  onClose,
  originalContent,
  enhancedContent,
  onAccept,
  onDiscard,
}: EnhanceCompareModalProps) => {
  if (!isOpen) return null;

  // The enhanced side comes back as HTML (<p>…</p><p>…</p> with no newlines);
  // the original side is plain text with newline separators. formatReviewHtml
  // handles both so paragraphs render distinctly on each side.
  const formatContent = (content: string) => formatReviewHtml(content);

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center p-4">
      <div data-tour="enhance-compare" className="w-[760px] max-w-[90vw] max-h-[90vh] bg-base-100 rounded-xl shadow-2xl border border-base-300 overflow-hidden flex flex-col animate-[fadeSlideUp_0.25s_ease-out]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-base-200 dark:border-base-300 bg-base-200/80 select-none">
          <div className="flex items-center gap-3">
            <h4 className="text-sm font-semibold text-gray-800 dark:text-base-content/80 m-0">
              Review Enhancement
            </h4>
          </div>
          <button
            onClick={onClose}
            className="text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 transition-colors"
            title="Close"
          >
            <XMarkIcon className="size-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-auto">
          <p className="text-sm font-medium text-gray-700 dark:text-base-content/80 mb-3 m-0">
            Compare the original text with the enhanced version.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <p className="text-xs text-base-content/40 m-0">Original</p>
              <div className="bg-base-200 border border-base-300 rounded-lg px-4 py-3">
                <div
                  className="text-sm text-gray-700 dark:text-base-content/80 leading-relaxed prose prose-sm max-w-none [&_p]:my-0 [&_p]:mb-3 [&_p:last-child]:mb-0"
                  dangerouslySetInnerHTML={{
                    __html: formatContent(originalContent),
                  }}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <p className="text-xs text-base-content/40 m-0">Enhanced</p>
              <div className="bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-900 rounded-lg px-4 py-3">
                <div
                  className="text-sm text-gray-700 dark:text-base-content/80 leading-relaxed prose prose-sm max-w-none [&_p]:my-0 [&_p]:mb-3 [&_p:last-child]:mb-0"
                  dangerouslySetInnerHTML={{
                    __html: formatContent(enhancedContent),
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-base-200 dark:border-base-300 bg-base-200/50">
          <Button variant="outline" size="sm" onClick={onDiscard}>
            Discard
          </Button>
          <Button variant="primary" size="sm" data-tour="enhance-accept" onClick={onAccept}>
            Accept Changes
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default EnhanceCompareModal;
